import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FEATURE_KEYS } from '../models/feature-flag.model';
import { normalizeUserRole, UserRole } from '../enums/user-role.enum';
import { AuthService } from '../services/auth.service';
import { FeatureFlagService } from '../services/feature-flag.service';

@Injectable({ providedIn: 'root' })
export class FeatureGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private features: FeatureFlagService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    if (!this.authService.isAuthenticated()) {
      return this.router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
    }

    const user = this.authService.getCurrentUser();
    const role = normalizeUserRole(user?.userRoleName || user?.userRole || user?.userRoleID);
    const featureKey = String(route.data['featureKey'] || FEATURE_KEYS.operatorBookingManagement);
    if (featureKey === FEATURE_KEYS.operatorBookingManagement && role === UserRole.PASSENGER) return true;

    return this.features.loadEffectiveFeatures().pipe(
      map(() => this.features.isEnabled(featureKey)
        ? true
        : this.unavailableRedirect(featureKey)),
      catchError(() => of(this.unavailableRedirect(featureKey)))
    );
  }

  private unavailableRedirect(featureKey: string): UrlTree {
    const message = featureKey === FEATURE_KEYS.passengerProfiles
      ? 'Passenger Profiles is currently unavailable.'
      : 'Booking Management is currently unavailable.';
    return this.router.createUrlTree(['/dashboard'], {
      queryParams: { availabilityMessage: message }
    });
  }
}
